import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FacilityContext, FacilityContextType } from './FacilityContext';
import UserContext from '../../../contexts/UserContext';
import { fetchFacilityDetails } from '../../services/Facilities';
import sendToast from '../../../utils/Toast';

const timezoneFromDetails = (details: any): string => (
    typeof details?.FacilityTimeZone === 'string' ? details.FacilityTimeZone : ''
);

const samePayload = (current: unknown, next: unknown): boolean => {
    if (current === next) return true;
    try {
        return JSON.stringify(current) === JSON.stringify(next);
    } catch {
        return false;
    }
};

// Neighbors pages read this payload, and the neighbors header reads isMailIn from it.
const shouldLoadFacilityDetails = process.env.REACT_APP_PLATFORM === 'neighbors';

export const FacilityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const {
        userObject: {
            registrants: { list: listOfResidents, activeRegistrantsIndexes },
        },
    } = useContext(UserContext);

    const activeResident = listOfResidents[activeRegistrantsIndexes[0]];
    const facilityId = activeResident?.Facility as string | undefined;

    const [facilityDetails, setFacilityDetails] = useState<any>(null);
    // Which facility the stored details belong to. A refresh of that same facility
    // keeps the screens' data in place instead of blanking it.
    const [loadedFacilityId, setLoadedFacilityId] = useState<string | undefined>(undefined);
    const loadedFacilityIdRef = useRef<string | undefined>(undefined);
    const detailsRef = useRef<any>(null);
    const facilityIdRef = useRef(facilityId);
    const requestIdRef = useRef(0);
    facilityIdRef.current = facilityId;

    const refresh = useCallback(async () => {
        const id = facilityIdRef.current;
        if (!id || !shouldLoadFacilityDetails) {
            requestIdRef.current += 1;
            loadedFacilityIdRef.current = undefined;
            setLoadedFacilityId(undefined);
            detailsRef.current = null;
            setFacilityDetails(null);
            return;
        }

        const requestId = ++requestIdRef.current;
        const isFirstLoad = loadedFacilityIdRef.current !== id;
        if (isFirstLoad) {
            detailsRef.current = null;
            setFacilityDetails(null);
        }

        try {
            const details = await fetchFacilityDetails(id);
            if (requestId !== requestIdRef.current) return;
            loadedFacilityIdRef.current = id;
            if (!samePayload(detailsRef.current, details)) {
                detailsRef.current = details;
                setFacilityDetails(details);
            }
            setLoadedFacilityId(id);
            if (isFirstLoad && !timezoneFromDetails(details)) {
                sendToast('error', 'Unable to fetch timezone of the facility. Please contact support.');
            }
        } catch (error) {
            if (requestId !== requestIdRef.current) return;
            if (loadedFacilityIdRef.current !== id) {
                detailsRef.current = null;
                setFacilityDetails(null);
            }
            // The attempt finished, so loading can drop even though there are no details.
            loadedFacilityIdRef.current = id;
            setLoadedFacilityId(id);
            if (isFirstLoad) {
                sendToast('error', error instanceof Error ? error.message : 'Failed to fetch facility information');
            }
        }
    }, []);

    useEffect(() => {
        refresh();
        return () => {
            requestIdRef.current += 1;
        };
    }, [facilityId, refresh]);

    // True on the same render the facility id appears, before the effect starts the request.
    const loading = shouldLoadFacilityDetails && !!facilityId && loadedFacilityId !== facilityId;

    const facilityTimezone = timezoneFromDetails(facilityDetails);

    const contextValue: FacilityContextType = useMemo(() => ({
        facilityDetails,
        facilityTimezone,
        loading,
        refresh,
    }), [facilityDetails, facilityTimezone, loading, refresh]);

    return (
        <FacilityContext.Provider value={contextValue}>
            {children}
        </FacilityContext.Provider>
    );
};
